import { INIT_COMBINED_GRAPHICAL_METRICS_DATA } from 'modules/Telematics/components/TelematicsRightPanel/LiveSummary/constants';
import {
	Accident,
	Battery,
	Engine,
	Sprayer,
	MetricSummary,
	EngineStatus,
	SprayerStatus,
	CombinedMetricsData,
	CombinedGraphicalMetricData,
	TimeSlot,
	InternalBatteryStatus,
	MetricTypesEnum,
	FloatMetric,
	BasicMetricData,
	ExternalPower,
} from 'modules/Telematics/components/TelematicsRightPanel/LiveSummary/types';
import { DATE_FORMATS } from 'modules/Telematics/constants';
import moment from 'moment';
import { cloneDeep } from 'lodash';

function findCommonIntervals(
	metric1: BasicMetricData[],
	metric2: BasicMetricData[],
) {
	const commonIntervals: Array<TimeSlot> = [];

	metric1.forEach((m1) => {
		const m1StartTime = moment(m1.startTime);
		const m1EndTime = moment(m1.endTime);

		metric2.forEach((m2) => {
			const m2StartTime = moment(m2.startTime);
			const m2EndTime = moment(m2.endTime);

			// Check for overlap between intervals
			if (m1StartTime <= m2EndTime && m1EndTime >= m2StartTime) {
				// Find the intersection of the intervals
				const commonStartTime = Math.max(
					m1StartTime.valueOf(),
					m2StartTime.valueOf(),
				);
				const commonEndTime = Math.min(
					m1EndTime.valueOf(),
					m2EndTime.valueOf(),
				);
				commonIntervals.push({
					startTime: {
						seconds: commonStartTime / 1000,
					},
					duration: {
						seconds: (commonEndTime - commonStartTime) / 1000,
					},
				});
			}
		});
	});

	return commonIntervals;
}

function calculateTimeSlots(timeSlot: TimeSlot) {
	const startTimeMoment = moment(timeSlot.startTime.seconds * 1000);
	const durationInSeconds = timeSlot.duration.seconds;
	const endTimeMoment = moment(startTimeMoment).add(
		durationInSeconds,
		'seconds',
	);
	const startOfDay = moment(startTimeMoment).startOf('day');
	const height = Math.floor(durationInSeconds / 180);
	const offset = Math.floor(
		startTimeMoment.diff(startOfDay, 'seconds') / 180,
	);

	return {
		startTime: startTimeMoment.format(
			DATE_FORMATS.DATE_MONTH_YEAR_AND_TIME,
		),
		endTime: endTimeMoment.format(DATE_FORMATS.DATE_MONTH_YEAR_AND_TIME),
		height,
		offset,
		metadata: [
			{
				label: 'Start',
				value: startTimeMoment.format(
					DATE_FORMATS.DATE_MONTH_YEAR_AND_TIME,
				),
			},
			{
				label: 'End',
				value: endTimeMoment.format(
					DATE_FORMATS.DATE_MONTH_YEAR_AND_TIME,
				),
			},
		],
	};
}

const generateCumulativeMetricsDataFromCombinedSingleMetricData = (
	data: CombinedMetricsData,
): CombinedGraphicalMetricData => {
	let copy = cloneDeep(INIT_COMBINED_GRAPHICAL_METRICS_DATA);
	copy.ENGINE_OFF.graphData = data.ENGINE_OFF;
	copy.ENGINE_ON.graphData = data.ENGINE_ON;
	copy.SPRAYER_ON.graphData = data.SPRAYER_ON;
	copy.ACCIDENT_STATUS.graphData = data.ACCIDENT_STATUS;
	copy.INTERNAL_BATTERY_LOW.graphData = data.INTERNAL_BATTERY_LOW;
	copy.MACHINE_MOVING.graphData = data.MACHINE_MOVING;
	copy.EXTERNAL_POWER_DISCONNECTED.graphData =
		data.EXTERNAL_POWER_DISCONNECTED;
	// --------------------------------------------------------------------
	const machineMovingWithInternalBatteryLow = findCommonIntervals(
		data.MACHINE_MOVING,
		data.INTERNAL_BATTERY_LOW,
	);
	copy.INTERNAL_BATTERY_LOW_AND_MOVING.graphData =
		machineMovingWithInternalBatteryLow.map((m) => calculateTimeSlots(m));
	// --------------------------------------------------------------------
	const machineMovingWithSprayerOn = findCommonIntervals(
		data.MACHINE_MOVING,
		data.SPRAYER_ON,
	);
	copy.SPRAYER_ON_AND_MOVING.graphData = machineMovingWithSprayerOn.map((m) =>
		calculateTimeSlots(m),
	);
	// --------------------------------------------------------------------
	const machineMovingWithExternalPowerDisconnected = findCommonIntervals(
		data.MACHINE_MOVING,
		data.EXTERNAL_POWER_DISCONNECTED,
	);
	copy.EXTERNAL_POWER_DISCONNECTED_AND_MOVING.graphData =
		machineMovingWithExternalPowerDisconnected.map((m) =>
			calculateTimeSlots(m),
		);
	return copy;
};

function processSpeed(
	metrics: FloatMetric[],
	combinedMetricsData: CombinedMetricsData,
) {
	metrics.forEach((m) => {
		if (m.value > 0.1) {
			let d = calculateTimeSlots(m.timeSlot);
			combinedMetricsData.MACHINE_MOVING.push(d);
		}
	});
}
function processAccident(
	metric: Accident[],
	combinedMetricsData: CombinedMetricsData,
) {
	metric.forEach((m) => {
		if (m.accidentStatus) {
			let d = calculateTimeSlots(m.timeSlot);
			if (d.startTime) d.height = 10;
			combinedMetricsData.ACCIDENT_STATUS.push(d);
		}
	});
}
function processBattery(
	metric: Battery[],
	combinedMetricsData: CombinedMetricsData,
) {
	metric.forEach((m) => {
		if (
			m.internalBatteryStatus === InternalBatteryStatus.LowBattery ||
			m.internalBatteryStatus ===
				InternalBatteryStatus.InternalBatteryTampered
		) {
			const d = calculateTimeSlots(m.timeSlot);
			combinedMetricsData.INTERNAL_BATTERY_LOW.push(d);
		}
	});
}
// function processDeviceHealth(metric: DeviceHealth[]) {}

function processEngine(
	metric: Engine[],
	combinedMetricsData: CombinedMetricsData,
) {
	metric.forEach((m) => {
		if (m.engineStatus === EngineStatus.EngineOn) {
			const d = calculateTimeSlots(m.timeSlot);
			combinedMetricsData.ENGINE_ON.push(d);
		} else if (m.engineStatus === EngineStatus.EngineOff) {
			const d = calculateTimeSlots(m.timeSlot);
			combinedMetricsData.ENGINE_OFF.push(d);
		}
	});
}
function processExternalPower(
	metric: ExternalPower[],
	combinedMetricsData: CombinedMetricsData,
) {
	metric.forEach((m) => {
		if (m.externalPowerStatus === 'EXTERNAL_POWER_REMOVED') {
			const d = calculateTimeSlots(m.timeSlot);
			combinedMetricsData.EXTERNAL_POWER_DISCONNECTED.push(d);
		}
	});
}

function processSprayer(
	metric: Sprayer[],
	combinedMetricsData: CombinedMetricsData,
) {
	metric.forEach((m) => {
		if (m.sprayerStatus === SprayerStatus.SprayerOn) {
			const d = calculateTimeSlots(m.timeSlot);
			combinedMetricsData.SPRAYER_ON.push(d);
		} else if (m.sprayerStatus === SprayerStatus.SprayerOff) {
			const d = calculateTimeSlots(m.timeSlot);
			combinedMetricsData.SPRAYER_OFF.push(d);
		}
	});
}
export const processMetricsDataList = (metricSummaryList: MetricSummary[]) => {
	let combinedMetricsData: CombinedMetricsData = {
		ENGINE_ON: [],
		ENGINE_OFF: [],
		SPRAYER_ON: [],
		SPRAYER_OFF: [],
		ACCIDENT_STATUS: [],
		INTERNAL_BATTERY_LOW: [],
		MACHINE_MOVING: [],
		EXTERNAL_POWER_DISCONNECTED: [],
	};
	metricSummaryList.forEach((metricSummary) => {
		switch (metricSummary.metricType) {
			case MetricTypesEnum.METRIC_TYPE_ACCIDENT_STATUS:
				processAccident(metricSummary.accident, combinedMetricsData);
				break;
			case MetricTypesEnum.METRIC_TYPE_INTERNAL_BATTERY_STATUS:
				processBattery(metricSummary.battery, combinedMetricsData);
				break;
			case MetricTypesEnum.METRIC_TYPE_ENGINE:
				processEngine(metricSummary.engine, combinedMetricsData);
				break;
			case MetricTypesEnum.METRIC_TYPE_SPRAYER:
				processSprayer(metricSummary.sprayer, combinedMetricsData);
				break;
			case MetricTypesEnum.METRIC_TYPE_SPEED:
				processSpeed(metricSummary.floatMetrics, combinedMetricsData);
				break;
			case MetricTypesEnum.METRIC_TYPE_EXTERNAL_POWER_STATUS:
				processExternalPower(
					metricSummary.externalPower,
					combinedMetricsData,
				);
				break;
			default:
		}
	});
	return generateCumulativeMetricsDataFromCombinedSingleMetricData(
		combinedMetricsData,
	);
};
